import React, { useState, useEffect } from 'react';
import { Calculator, DollarSign, Percent } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const MarketShareCalculator = () => {
  const [companyRevenue, setCompanyRevenue] = useState(1000000);
  const [totalMarketRevenue, setTotalMarketRevenue] = useState(10000000);
  const [marketShare, setMarketShare] = useState(0);

  useEffect(() => {
    calculateMarketShare();
  }, [companyRevenue, totalMarketRevenue]);

  const calculateMarketShare = () => {
    const share = (companyRevenue / totalMarketRevenue) * 100;
    setMarketShare(parseFloat(share.toFixed(2)));
  };

  const siteUrl = 'https://www.marketingcalculatorhub.com';
  const pageUrl = `${siteUrl}/market-share-calculator`;
  const logoUrl = `${siteUrl}/logo.png`;

  return (
    <div className="container mx-auto px-4 py-8">
      <Helmet>
        <title>Market Share Calculator: Measure Industry Position | Free Tool</title>
        <meta name="description" content="Calculate your Market Share instantly. Compare with industry benchmarks, get optimization tips, and improve competitive positioning." />
        <meta name="keywords" content="market share calculator, competitive analysis, industry metrics, market position, business analytics" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content="Free Market Share Calculator: Optimize Competitive Position" />
        <meta property="og:description" content="Calculate your Market Share instantly. Get actionable insights to improve market position and competitive advantage." />
        <meta property="og:image" content={logoUrl} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content="Free Market Share Calculator: Optimize Competitive Position" />
        <meta property="twitter:description" content="Calculate your Market Share instantly. Get actionable insights to improve market position and competitive advantage." />
        <meta property="twitter:image" content={logoUrl} />

        {/* Schema.org JSON-LD */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Market Share Calculator",
            "description": "Free calculator to measure and optimize market share and competitive position",
            "url": pageUrl,
            "image": logoUrl,
            "applicationCategory": "BusinessApplication",
            "operatingSystem": "Any",
            "offers": {
              "@type": "Offer",
              "price": "0",
              "priceCurrency": "USD"
            },
            "featureList": [
              "Calculate Market Share",
              "Industry Benchmarks",
              "Competitive Analysis",
              "Performance Analysis",
              "Best Practices"
            ],
            "about": {
              "@type": "Thing",
              "name": "Market Share",
              "description": "The percentage of total market revenue captured by a company, indicating competitive position."
            },
            "audience": {
              "@type": "Audience",
              "audienceType": "Business Strategists, Market Analysts, Executives"
            },
            "mainEntity": {
              "@type": "HowTo",
              "name": "How to Calculate Market Share",
              "step": [
                {
                  "@type": "HowToStep",
                  "text": "Enter company revenue"
                },
                {
                  "@type": "HowToStep",
                  "text": "Input total market revenue"
                },
                {
                  "@type": "HowToStep",
                  "text": "View your market share and optimization tips"
                }
              ]
            }
          })}
        </script>
      </Helmet>

      <h1 className="text-3xl font-bold text-gray-800 mb-6 flex items-center">
        <Calculator className="w-8 h-8 mr-2 text-indigo-600" />
        Market Share Calculator
      </h1>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Input Values</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Company Revenue</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={companyRevenue}
                  onChange={(e) => setCompanyRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="1000000"
                />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Total Market Revenue</label>
              <div className="mt-1 relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  value={totalMarketRevenue}
                  onChange={(e) => setTotalMarketRevenue(Number(e.target.value))}
                  className="focus:ring-indigo-500 focus:border-indigo-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md"
                  placeholder="10000000"
                />
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Results</h2>
          <div className="text-4xl font-bold text-indigo-600 mb-2 flex items-center">
            {marketShare}%
            <Percent className="w-8 h-8 ml-2 text-indigo-400" />
          </div>
          <p className="text-gray-600">Market Share</p>
        </div>
      </div>

      <div className="mt-8 bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Interpretation & Industry Benchmarks</h2>
        <p className="text-gray-600">
          Your Market Share is {marketShare}%. This means your company's revenue represents {marketShare}% of the total market revenue.
        </p>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Market Position Categories</h3>
        <ul className="list-disc list-inside text-gray-600 mb-4">
          <li>Market Leader: Greater than 40%</li>
          <li>Market Challenger: 20-40%</li>
          <li>Market Follower: 10-20%</li>
          <li>Market Nicher: 5-10%</li>
          <li>Market Entrant: Less than 5%</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Growth Strategies</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Innovate products/services</li>
          <li>Improve quality</li>
          <li>Optimize pricing</li>
          <li>Expand markets</li>
          <li>Boost brand awareness</li>
          <li>Form partnerships</li>
          <li>Enhance retention</li>
          <li>Acquire competitors</li>
          <li>Differentiate offerings</li>
          <li>Target new segments</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Competitive Advantages</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Product Innovation</li>
          <li>Cost Leadership</li>
          <li>Brand Strength</li>
          <li>Customer Service</li>
          <li>Distribution Network</li>
          <li>Technology Edge</li>
        </ul>

        <h3 className="text-lg font-semibold text-gray-800 mt-4 mb-2">Impact Analysis</h3>
        <ul className="list-disc list-inside text-gray-600">
          <li>Product innovation: +5-10% share</li>
          <li>Market expansion: +3-7% share</li>
          <li>Strategic acquisition: +10-15% share</li>
          <li>Brand enhancement: +2-5% share</li>
          <li>Customer loyalty: +3-6% share</li>
        </ul>
      </div>
    </div>
  );
};

export default MarketShareCalculator;
